import { memo } from 'react';
// eslint-disable-next-line @direct-frontend/modules/no-restricted-entity-imports -- все тут норм
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { HStack, type TestProps } from '@direct-frontend/components';
import { AdaptiveMedia } from '#shared/components/AdaptiveImage';
import { ImageViewer } from '#shared/components/ImageViewer';
import { MediaGalleryImage, SortableMediaGalleryItem } from '#shared/components/MediaV2GalleryItem';
// eslint-disable-next-line @direct-frontend/modules/no-restricted-entity-imports -- типы поля медиа
import { type MediaV2Field } from '#shared/lib/form';
import { useProductDraggableGalleryMediaField } from '../../hooks/useProductDraggableGalleryMediaField';
import { ProductDraggableGalleryMediaFieldAddButton } from './ProductDraggableGalleryMediaFieldAddButton';
import { ProductDraggableGalleryMediaFieldViewerItem } from './ProductDraggableGalleryMediaFieldViewerItem';
import styles from './ProductDraggableGalleryMediaField.module.css';

export interface ProductDraggableGalleryMediaFieldProps extends TestProps {
    media: MediaV2Field[];
    removeMedia: (index: number) => void;
    setMedia: (next: MediaV2Field[]) => void;
    withAdding?: boolean;
    itemWidth?: number;
    itemHeight?: number;
    onAddPhoto?: () => void;
    onAddVideo?: () => void;
    /**
     * Без DnD, без удаления и без кнопки добавления (например, вариант в архиве)
     */
    isDisabled?: boolean;
    /** В модалке надо отключать overlay, dnd-kit неверно считает изнутри модалки */
    withoutDragOverlay?: boolean;
}

/** Сетка медиа варианта с DnD, просмотром и опциональным добавлением фото/видео. */
export const ProductDraggableGalleryMediaField = memo(function ProductDraggableGalleryMediaField({
    media,
    removeMedia,
    setMedia,
    withAdding,
    itemWidth = 120,
    itemHeight = 120,
    onAddPhoto,
    onAddVideo,
    isDisabled = false,
    withoutDragOverlay,
    'data-testid': dataTestId = 'ProductDraggableGalleryMediaField',
}: ProductDraggableGalleryMediaFieldProps) {
    const {
        isMultipleMedia,
        sensors,
        draggableId,
        draggableItem,
        handleDragEnd,
        handleDragStart,
        handleOpenImage,
        handleDeleteImage,
        changeIndex,
        closeViewer,
        isViewerOpen,
        index,
    } = useProductDraggableGalleryMediaField({
        media,
        removeMedia,
        setMedia,
    });

    const galleryItems = media.map((item, openAtIndex) => (
        <SortableMediaGalleryItem
            width={itemWidth}
            height={itemHeight}
            onClick={() => !draggableId && handleOpenImage(openAtIndex)}
            key={item.id}
            onDeleteImage={isDisabled ? undefined : () => handleDeleteImage(openAtIndex)}
            media={item}
            data-testid={`${dataTestId}.GalleryItem.${item.id}`}
        />
    ));

    const content = isMultipleMedia ? (
        <DndContext
            sensors={sensors}
            onDragEnd={isDisabled ? undefined : handleDragEnd}
            onDragStart={isDisabled ? undefined : handleDragStart}
        >
            <SortableContext items={media} strategy={rectSortingStrategy}>
                {galleryItems}
                <DragOverlay>
                    {!withoutDragOverlay && draggableId && draggableItem ? (
                        <MediaGalleryImage width={itemWidth} height={itemHeight} media={draggableItem} />
                    ) : null}
                </DragOverlay>
            </SortableContext>
        </DndContext>
    ) : (
        galleryItems
    );

    return (
        <HStack data-testid={dataTestId}>
            <HStack
                gap="2"
                flexWrap="wrap"
                className={isMultipleMedia ? styles.cursorGrab : undefined}
                data-testid={`${dataTestId}.ListItems`}
            >
                {content}

                {withAdding && onAddPhoto && onAddVideo && !isDisabled ? (
                    <ProductDraggableGalleryMediaFieldAddButton
                        itemWidth={itemWidth}
                        itemHeight={itemHeight}
                        onAddPhoto={onAddPhoto}
                        onAddVideo={onAddVideo}
                        media={media}
                        data-testid={`${dataTestId}.AddButton`}
                    />
                ) : null}
            </HStack>
            {media.length > 0 && (
                <ImageViewer
                    open={isViewerOpen}
                    currentIndex={index}
                    count={media.length}
                    renderMainImage={(viewerIndex) => (
                        <HStack justifyContent="center" className={styles.wrapper}>
                            <ProductDraggableGalleryMediaFieldViewerItem media={media[viewerIndex]} />
                        </HStack>
                    )}
                    renderThumbnail={(viewerIndex) => (
                        <AdaptiveMedia
                            media={media[viewerIndex]}
                            alt="Превью"
                            width={90}
                            height={120}
                            fit="cover"
                            background="gray"
                        />
                    )}
                    onIndexChange={changeIndex}
                    onClose={closeViewer}
                    data-testid={`${dataTestId}.ImageViewer`}
                />
            )}
        </HStack>
    );
});
